import { type AppScenario, picker, section, tab, table, toned } from './build.ts';

const COMPANY_PARTS = ['Atelier', 'Nova', 'Ligne', 'Ponant', 'Cobalt', 'Hélix', 'Boréal', 'Sillage', 'Quartz', 'Méridien', 'Talus', 'Archipel'];
const SUFFIXES = ['Logiciels', 'Industrie', 'Conseil', 'Santé', 'Mobilité', 'Data', 'Énergie', 'Services'];
const ACTIVITIES = ['Logiciel B2B de gestion des stocks', 'Plateforme RH pour PME', 'Maintenance industrielle', 'Paiement pour commerçants', 'Logistique urbaine', 'Cybersécurité managée', 'Formation professionnelle', 'Énergie pour sites industriels'];
const CITIES = ['Paris', 'Lyon', 'Nantes', 'Bordeaux', 'Lille', 'Rennes', 'Toulouse', 'Marseille'];
const BANDS = ['10 à 19 salariés (2024)', '20 à 49 salariés (2024)', '50 à 99 salariés (2024)', '100 à 199 salariés (2024)', '250 à 499 salariés (2024)', null];
const JOBS = ['Account Executive', 'SDR', 'BDR', 'Account Executive, SDR', 'Head of Sales', 'SDR (alternance)'];

const sourcingRows = () => {
  const p = picker(7);
  return Array.from({ length: 120 }, (_, index) => {
    const band = p.pick(BANDS);
    const decision = band === '250 à 499 salariés (2024)'
      ? 'Écartée : plus de 250 salariés'
      : band === null ? 'À vérifier : effectif non déclaré au registre' : p.next() < 0.75 ? 'Retenue' : 'Écartée : activité B2C';
    const name = `${p.pick(COMPANY_PARTS)} ${p.pick(SUFFIXES)}${index > 95 ? ` ${index}` : ''}`;
    return {
      company: name,
      activity: p.pick(ACTIVITIES),
      size: band,
      city: p.pick(CITIES),
      jobs: `${p.pick(JOBS)} — offre du ${p.int(1, 28)}/09/2026`,
      decision,
      reason: decision === 'Retenue'
        ? 'Éditeur B2B en croissance ; deux recrutements commerciaux ouverts, l’équipe devra intégrer de nouveaux arrivants rapidement.'
        : 'Hors cible du Profil commercial.',
      next: decision === 'Retenue' ? 'Qualifier le compte, puis proposer le diagnostic' : '—',
      source: `https://carrieres.example/${index + 1}`,
    };
  });
};

/** The 120 fictional companies of the sourcing scenario (also given to the model by the lab proposal script). */
export const SOURCING_ROWS = sourcingRows();
const countDecision = (prefix: string) => SOURCING_ROWS.filter((row) => row.decision.startsWith(prefix)).length;
const DECISION_TONE = (decision: string) => decision === 'Retenue' ? 'good' : decision.startsWith('À vérifier') ? 'warning' : 'neutral';

export const sellerScenarios: AppScenario[] = [
  {
    id: 'sourcing',
    title: 'Liste de prospection (recrutements commerciaux)',
    job: 'Le commercial, le lundi, choisit les comptes à contacter dans la semaine.',
    stresses: ['tableau large (10 colonnes)', '120 lignes', 'cellules longues', 'statuts en texte libre', 'liens', 'bandeau de chiffres', 'pastilles de sélection'],
    wished: ['ajouter au CRM par ligne'],
    payload: {
      status: 'complete',
      tabs: [tab('result', 'Entreprises', [
        section('summary', 'Cette semaine', 'summary', 'metric_grid', {
          presentation: 'band',
          metrics: [
            { label: 'Entreprises trouvées', value: SOURCING_ROWS.length },
            { label: 'Retenues', value: countDecision('Retenue'), status: 'good' },
            { label: 'À vérifier', value: countDecision('À vérifier'), status: 'warning' },
            { label: 'Nouvelles depuis lundi dernier', value: 23, trend: 'up', trend_value: '+8' },
          ],
        }),
        section('companies', 'Entreprises à examiner', 'detail', 'data_table', table('Entreprises à examiner', [
          // Decision right after the name (Forge instruction): a wide table scrolls sideways.
          ['company', 'Entreprise'], ['decision', 'Sélection'], ['reason', 'Motif'], ['activity', 'Activité'], ['size', 'Effectif'],
          ['city', 'Siège'], ['jobs', 'Postes ouverts'], ['next', 'Prochaine action'], ['source', 'Source'],
        ], SOURCING_ROWS.map((row) => ({ ...row, decision: toned(row.decision, DECISION_TONE(row.decision)) })), { source_note: 'Recherche web et registre officiel des entreprises, lus le 02/10/2026.' })),
      ])],
    },
  },
  {
    id: 'meeting-prep',
    title: 'Préparation de rendez-vous',
    job: 'Le commercial, 30 minutes avant un premier rendez-vous, veut savoir quoi dire et quoi demander.',
    stresses: ['plusieurs vues de nature différente sur une page', 'document long en markdown', 'fiche entité', 'liste de questions'],
    wished: ['fiche du compte et brief côte à côte', 'questions cochables pendant le rendez-vous', 'lien vers les derniers emails'],
    payload: {
      status: 'complete',
      tabs: [
        tab('brief', 'Brief', [
          section('account', 'Le compte', 'context', 'entity_summary', {
            entity: { name: 'Cobalt Mobilité', type: 'company' },
            headline: 'Logistique urbaine, 85 salariés, deux SDR recrutés en septembre.',
            key_facts: [
              { label: 'Siège', value: 'Lyon' }, { label: 'Effectif', value: '50 à 99 salariés (2024)' },
              { label: 'CRM', value: 'HubSpot (d’après l’offre SDR)' }, { label: 'Décideur', value: 'Directrice commerciale' },
            ],
            tags: ['B2B', 'Croissance', 'Premier contact'],
            recent_activity: [
              { date: '2026-09-29', summary: 'Réponse à la prise de contact : ouverte à un échange de 30 minutes.' },
              { date: '2026-09-18', summary: 'Publication de deux offres SDR à Lyon.' },
            ],
          }, 'narrow'),
          section('brief', 'Ce qu’il faut dire', 'artifact', 'artifact_document', {
            title: 'Brief — Cobalt Mobilité',
            subject: 'Premier rendez-vous, jeudi 10 h',
            body_format: 'markdown',
            body: [
              '## En une phrase', 'Cobalt recrute deux SDR : leur intégration est le sujet du moment, pas l’outillage.', '',
              '## Angle', '- Partir du recrutement observé (deux offres du 18/09).', '- Poser la question du délai de montée en compétence.', '- Proposer le diagnostic flash, sans présumer d’une douleur.', '',
              '## À éviter', '- Parler de remplacement du CRM.', '- Citer des chiffres de productivité non sourcés.',
            ].join('\n'),
            meta_items: [{ label: 'Durée', value: '30 min' }, { label: 'Sources', value: 'Offres, site, échange du 29/09' }],
            export_actions: ['copy_markdown'],
          }, 'wide'),
        ]),
        tab('questions', 'Questions', [
          section('questions', 'Questions à poser', 'actions', 'action_list', {
            title: 'Questions à poser',
            items: [
              { id: 'q1', label: 'Combien de temps pour qu’un nouveau SDR soit autonome ?', priority: 'high', status: 'pending', category: 'Découverte' },
              { id: 'q2', label: 'Qui forme les nouveaux arrivants aujourd’hui ?', priority: 'high', status: 'pending', category: 'Découverte' },
              { id: 'q3', label: 'Quels outils les SDR utilisent-ils au quotidien ?', priority: 'medium', status: 'pending', category: 'Contexte' },
              { id: 'q4', label: 'Qui d’autre décide sur ce sujet ?', priority: 'medium', status: 'pending', category: 'Décision' },
            ],
          }),
          section('contacts', 'Personnes à connaître', 'context', 'data_table', table('Personnes à connaître', [
            ['role', 'Rôle'], ['why', 'Pourquoi'], ['source', 'Source'],
          ], [
            { role: 'Directrice commerciale', why: 'Décide du recrutement et de l’onboarding', source: 'Site, page équipe' },
            { role: 'Responsable SDR', why: 'Vit la montée en compétence au quotidien', source: 'Offre SDR' },
          ])),
        ]),
      ],
    },
  },
  {
    id: 'follow-ups',
    title: 'Mes relances de la semaine',
    job: 'Le commercial, chaque matin, traite ses relances dans l’ordre.',
    stresses: ['liste d’actions longue', 'priorités et échéances', 'statuts mélangés'],
    wished: ['regrouper par jour', 'marquer « fait » sans quitter la page', 'brouillon d’email par relance'],
    payload: {
      status: 'complete',
      tabs: [tab('result', 'Relances', [
        section('follow-ups', 'À relancer', 'actions', 'action_list', {
          title: 'À relancer',
          description: '14 relances, dont 4 en retard.',
          items: Array.from({ length: 14 }, (_, index) => ({
            id: `f${index}`,
            label: `${['Relancer', 'Envoyer la proposition à', 'Confirmer le rendez-vous avec', 'Répondre à'][index % 4]} ${COMPANY_PARTS[index % COMPANY_PARTS.length]} ${SUFFIXES[index % SUFFIXES.length]}`,
            priority: (['high', 'medium', 'low'] as const)[index % 3]!,
            status: (['pending', 'pending', 'in_progress', 'done'] as const)[index % 4]!,
            due: `2026-10-0${(index % 7) + 1}`,
            context: index % 3 === 0 ? 'Sans réponse depuis 6 jours après la démo.' : undefined,
            entity: { name: `${COMPANY_PARTS[index % COMPANY_PARTS.length]} ${SUFFIXES[index % SUFFIXES.length]}`, type: 'account' },
          })),
          summary: { total: 14, completed: 3, high_priority: 5 },
        }),
      ])],
    },
  },
];
